import React, { useEffect, useMemo, useRef, useState } from 'react';

import ReactDOM from 'react-dom';
import classNames from 'classnames';
import { usePopper } from 'react-popper';
import type { Placement } from '@popperjs/core/lib/enums';
import _isFunction from 'lodash/isFunction';

import useClickOutside from '../../hooks/use-click-outside';
import { TimeLineClusterItem, TimelineItem, TooltipRenderFunction } from '../../types';

import styles from './timeline-item-tooltip.module.scss';

export enum ToolTipPlacement {
	Right = 'right',
	Left = 'left',
	Top = 'top',
	Bottom = 'bottom'
}

const DEFAULT_TOOLTIP_PLACE: Placement = 'top';

const ARROW_SHADOW_WIDTH = 3;
const ARROW_SIZE = +styles.arrowSize + ARROW_SHADOW_WIDTH; // px

export type TooltipTranslations = { [key: string]: string };

export interface Props {
	item?: TimelineItem | TimeLineClusterItem;
	tooltipRender?: TooltipRenderFunction;
	targetElement: HTMLElement | null;
	removeTooltip: () => void;
	shouldShowTooltipArrow: boolean;
	tooltipContainerSelector?: string;
}

const TimeLineItemTooltip = React.memo(
	({
		item,
		targetElement,
		tooltipRender,
		removeTooltip,
		shouldShowTooltipArrow,
		tooltipContainerSelector
	}: Props) => {
		const [referenceElement, setReferenceElement] = useState(targetElement);
		const popperElement = useRef<HTMLDivElement>(null);
		const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>();
		const shouldRenderTooltip = targetElement && !!item && _isFunction(tooltipRender);
		const items = (item as TimeLineClusterItem)?.items || [item];

		useEffect(() => {
			setReferenceElement(targetElement);
		}, [targetElement]);

		useClickOutside(
			[popperElement.current, referenceElement],
			!!(shouldRenderTooltip && shouldShowTooltipArrow),
			removeTooltip
		);

		const {
			styles: popperStyle,
			attributes,
			update
		} = usePopper(referenceElement, popperElement.current, {
			placement: DEFAULT_TOOLTIP_PLACE,
			modifiers: [
				{
					name: 'flip',
					options: {
						fallbackPlacements: [ToolTipPlacement.Left, ToolTipPlacement.Right, ToolTipPlacement.Top]
					}
				},
				{
					name: 'offset',
					options: {
						offset: [0, ARROW_SIZE]
					}
				},
				{
					name: 'arrow',
					options: {
						element: arrowElement
					}
				}
			]
		});

		const arrowPlace = useMemo(() => {
			if (attributes && attributes['popper']) {
				const currentPlace = attributes['popper']['data-popper-placement'];
				if (currentPlace.startsWith(ToolTipPlacement.Left)) {
					return ToolTipPlacement.Right;
				} else if (currentPlace.startsWith(ToolTipPlacement.Right)) {
					return ToolTipPlacement.Left;
				} else if (currentPlace.startsWith(ToolTipPlacement.Top)) {
					return ToolTipPlacement.Bottom;
				} else if (currentPlace.startsWith(ToolTipPlacement.Bottom)) {
					return ToolTipPlacement.Top;
				}
			}
			return '';
		}, [attributes]);

		return (
			<>
				{ReactDOM.createPortal(
					<div
						ref={popperElement}
						className={classNames(styles.tooltipContainer, styles[arrowPlace])}
						style={popperStyle.popper}
						{...attributes.popper}
					>
						{shouldRenderTooltip && (
							<>
								{tooltipRender(items, { removeTooltip, redrew: update })}
								{shouldShowTooltipArrow && (
									<div
										ref={setArrowElement}
										data-popper-arrow
										data-test-id="tooltip-arrow"
										className={styles.tooltipArrow}
										style={popperStyle.arrow}
									/>
								)}
							</>
						)}
					</div>,
					(tooltipContainerSelector && document.querySelector(`.${tooltipContainerSelector}`)) ||
						document.body
				)}
			</>
		);
	}
);

export default TimeLineItemTooltip;
